import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import ProfileMenu from '../components/ProfileMenu';

type ParamValue = number | string | boolean;
type ParamField = {
  key: string;
  label: string;
  type: 'number' | 'boolean' | 'select' | 'string';
  default: ParamValue;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
  help?: string;
};
type StrategyInfo = { id: string; name: string; summary: string; params: ParamField[] };
type ProviderInfo = { id: string; name: string; description: string };
type Limit = { maxDays: number; note: string };
type Metrics = {
  netPnl: number;
  trades: number;
  wins: number;
  losses: number;
  winRate: number | null;
  avgR: number | null;
  profitFactor: number | null;
  expectancyR: number | null;
  expectancyGbp: number | null;
  maxDrawdown: number;
  maxDrawdownPct: number | null;
  sharpe: number | null;
  exposure: number;
  maxConsecutiveLosses: number;
  skipped: { riskCap: number; spread: number; invalid: number };
};
type Trade = {
  id: number;
  side: 'long' | 'short';
  entryTime: number;
  exitTime: number;
  entry: number;
  exit: number;
  stake: number;
  pnl: number;
  r: number | null;
  exitReason: string;
  reason: string;
};
type Segment = {
  label: string;
  metrics: Metrics;
  trades: Trade[];
  equity: { t: number; equity: number; dd: number; ddPct: number }[];
  monthly: { period: string; trades: number; wins: number; netPnl: number; avgR: number | null }[];
  yearly: { period: string; trades: number; wins: number; netPnl: number; avgR: number | null }[];
};
type RunResult = {
  summary: {
    symbol: string;
    name: string;
    timeframe: string;
    barCount: number;
    variantCount: number;
    spreadPoints: number;
    spreadNote: string;
    pointSize: number;
    focus: string;
    warnings: string[];
  };
  primary: Segment;
  full?: Segment;
  inSample?: Segment;
  outOfSample?: Segment;
  folds?: { index: number; params: Record<string, ParamValue>; train: Metrics; test: Metrics }[];
  sweep?: { count: number; rows: { params: Record<string, ParamValue>; trades: number; netPnl: number; expectancyR: number | null; outOfSampleNetPnl?: number }[] };
};
type RunView = {
  id: string;
  name: string;
  status: 'queued' | 'running' | 'done' | 'error';
  progress: { pct: number; message: string };
  createdAt: string;
  error: string | null;
  config: Record<string, unknown>;
  result?: RunResult;
  headline: { label: string; netPnl: number; trades: number; expectancyR: number | null } | null;
};

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const money = (n: number) => gbp.format(n);
const signed = (n: number) => (n > 0 ? `+${gbp.format(n)}` : n < 0 ? `−${gbp.format(Math.abs(n))}` : gbp.format(0));
const colour = (n: number) => (n > 0 ? 'text-emerald-600 dark:text-emerald-400' : n < 0 ? 'text-red-600 dark:text-red-400' : '');
const pct = (n: number | null) => (n === null ? '—' : `${(n * 100).toFixed(1)}%`);
const num = (n: number | null, digits = 2) => (n === null || !Number.isFinite(n) ? '—' : n.toFixed(digits));
const when = (ms: number) => new Date(ms).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function spanDays(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.floor((b - a) / 86_400_000) + 1;
}

async function api(path: string, opts?: RequestInit) {
  const res = await fetch(`/api/backtests${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export default function Backtests() {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [strategies, setStrategies] = useState<StrategyInfo[]>([]);
  const [limits, setLimits] = useState<Record<string, Record<string, Limit>>>({});
  const [quick, setQuick] = useState<{ symbol: string; label: string }[]>([]);
  const [provider, setProvider] = useState('yahoo');
  const [symbol, setSymbol] = useState('EURUSD=X');
  const [datasetId, setDatasetId] = useState<string | undefined>();
  const [hits, setHits] = useState<{ symbol: string; name: string; type: string; exchange: string }[]>([]);
  const [timeframe, setTimeframe] = useState('1h');
  const [from, setFrom] = useState(isoDaysAgo(728));
  const [to, setTo] = useState(todayIso());
  const [strategyId, setStrategyId] = useState('variant-a');
  const [params, setParams] = useState<Record<string, ParamValue>>({});
  const [sweeps, setSweeps] = useState<Record<string, { min: string; max: string; step: string }>>({});
  const [spread, setSpread] = useState('');
  const [spreadNote, setSpreadNote] = useState('');
  const [slippage, setSlippage] = useState('0');
  const [fundLong, setFundLong] = useState('0.075');
  const [fundShort, setFundShort] = useState('0.025');
  const [maxSpread, setMaxSpread] = useState('0.1');
  const [mode, setMode] = useState('risk_gbp');
  const [capital, setCapital] = useState('449');
  const [risk, setRisk] = useState('10');
  const [cap, setCap] = useState('15');
  const [stakeMin, setStakeMin] = useState('0.5');
  const [stakeMax, setStakeMax] = useState('2');
  const [fixedStake, setFixedStake] = useState('1');
  const [validation, setValidation] = useState('none');
  const [isFraction, setIsFraction] = useState('0.7');
  const [folds, setFolds] = useState('4');
  const [anchored, setAnchored] = useState(false);
  const [metric, setMetric] = useState('expectancyR');
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RunView | null>(null);
  const [history, setHistory] = useState<RunView[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [compare, setCompare] = useState<{ runs: { id: string; name: string; metrics: Metrics; label: string }[]; equity: Record<string, string | number>[] } | null>(null);
  const [segmentKey, setSegmentKey] = useState<'primary' | 'full' | 'inSample'>('primary');
  const [tradeLimit, setTradeLimit] = useState(40);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const strategy = strategies.find((item) => item.id === strategyId);
  const limit = limits[provider]?.[timeframe];
  const days = spanDays(from, to);
  const tooLong = Boolean(limit && days > limit.maxDays);

  useEffect(() => {
    api('/meta').then((meta) => {
      setProviders(meta.providers || []);
      setStrategies(meta.strategies || []);
      const map: Record<string, Record<string, Limit>> = {};
      for (const row of meta.limits || []) map[row.id] = row.limits;
      setLimits(map);
      setQuick(meta.quickSymbols || []);
    }).catch((error: Error) => setErr(error.message));
    refreshHistory();
  }, []);

  useEffect(() => {
    if (provider !== 'yahoo') return;
    const handle = setTimeout(() => {
      const q = symbol.trim();
      if (q.length < 1) { setHits([]); return; }
      api(`/symbols?provider=yahoo&q=${encodeURIComponent(q)}`)
        .then((data) => setHits(data.hits || []))
        .catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [symbol, provider]);

  useEffect(() => {
    if (provider !== 'yahoo' || !symbol.trim()) return;
    api(`/presets?symbol=${encodeURIComponent(symbol.trim())}`)
      .then((preset) => setSpreadNote(`${preset.note} Point size ${preset.pointSize}.`))
      .catch(() => setSpreadNote(''));
  }, [symbol, provider]);

  useEffect(() => {
    if (!runId) return undefined;
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        const data = await api(`/runs/${runId}`) as RunView;
        if (stop) return;
        setRun(data);
        if (data.status === 'queued' || data.status === 'running') timer = window.setTimeout(tick, 800);
        else refreshHistory();
      } catch (error) {
        if (!stop) setErr(error instanceof Error ? error.message : 'Could not read the run');
      }
    };
    timer = window.setTimeout(tick, 150);
    return () => { stop = true; window.clearTimeout(timer); };
  }, [runId]);

  async function refreshHistory() {
    try {
      const data = await api('/runs');
      setHistory(data.runs || []);
    } catch {
      /* history is optional on first paint */
    }
  }

  function buildBody() {
    const bodyParams: Record<string, ParamValue> = {};
    for (const field of strategy?.params || []) {
      if (params[field.key] !== undefined) bodyParams[field.key] = params[field.key];
    }
    const sweep: Record<string, { min: number; max: number; step: number }> = {};
    for (const [key, range] of Object.entries(sweeps)) {
      const min = Number(range.min);
      const max = Number(range.max);
      const step = Number(range.step);
      if (Number.isFinite(min) && Number.isFinite(max) && Number.isFinite(step) && step > 0) sweep[key] = { min, max, step };
    }
    const costs: Record<string, number | null> = {
      slippagePoints: Number(slippage),
      fundingAnnualLong: Number(fundLong),
      fundingAnnualShort: Number(fundShort),
      maxSpreadToStop: maxSpread.trim() === '' ? null : Number(maxSpread),
    };
    if (spread.trim() !== '') costs.spreadPoints = Number(spread);
    return {
      provider,
      symbol: symbol.trim(),
      datasetId,
      timeframe,
      from,
      to,
      strategy: strategyId,
      params: bodyParams,
      costs,
      sizing: {
        mode,
        startingCapital: Number(capital),
        riskGbp: Number(risk),
        riskCapGbp: Number(cap),
        stakeMin: Number(stakeMin),
        stakeMax: Number(stakeMax),
        fixedStake: Number(fixedStake),
      },
      validation: {
        mode: validation,
        isFraction: Number(isFraction),
        folds: Number(folds),
        anchored,
        selectionMetric: metric,
      },
      sweep: Object.keys(sweep).length ? sweep : undefined,
    };
  }

  async function onRun() {
    setErr('');
    setCompare(null);
    setSegmentKey('primary');
    setTradeLimit(40);
    setBusy(true);
    try {
      const created = await api('/runs', { method: 'POST', body: JSON.stringify(buildBody()) });
      setRun(null);
      setRunId(created.id);
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Could not start');
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(file: File) {
    setErr('');
    try {
      const csv = await file.text();
      const saved = await api('/csv', { method: 'POST', body: JSON.stringify({ name: file.name, csv }) });
      setProvider('csv');
      setDatasetId(saved.id);
      setSymbol(file.name);
      if (saved.from && saved.to) {
        setFrom(new Date(saved.from).toISOString().slice(0, 10));
        setTo(new Date(saved.to).toISOString().slice(0, 10));
      }
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Upload failed');
    }
  }

  async function onCompare() {
    setErr('');
    try {
      setCompare(await api('/compare', { method: 'POST', body: JSON.stringify({ ids: picked }) }));
    } catch (error) {
      setErr(error instanceof Error ? error.message : 'Compare failed');
    }
  }

  async function removeRun(id: string) {
    await api(`/runs/${id}`, { method: 'DELETE' });
    if (runId === id) { setRunId(null); setRun(null); }
    setPicked((curr) => curr.filter((item) => item !== id));
    refreshHistory();
  }

  const result = run?.result;
  const segment = result
    ? (segmentKey === 'full' && result.full) || (segmentKey === 'inSample' && result.inSample) || result.primary
    : null;
  const chart = useMemo(() => (segment?.equity || []).map((point, idx) => ({
    idx,
    equity: point.equity,
    dd: point.dd,
    when: new Date(point.t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }),
  })), [segment]);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <header className="sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50/95 px-3 py-3 dark:border-zinc-800 dark:bg-zinc-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold">Backtests</h1>
            <p className="text-xs text-zinc-500">Research only. This page never places an order and never logs in to IG.</p>
          </div>
          <span className="flex items-center gap-3">
            <Link to="/backtests/scan" className="text-xs underline">Research scan</Link>
            <Link to="/" className="text-xs underline">Console</Link>
            <ProfileMenu />
          </span>
        </div>
      </header>
      <main className="mx-auto grid max-w-6xl gap-4 px-3 py-4 lg:grid-cols-[360px_1fr]">
        <section className="space-y-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
          <Field label="Data">
            <Select value={provider} onValueChange={(value) => { setProvider(value); setDatasetId(undefined); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {providers.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Symbol">
            <Input value={symbol} onChange={(event) => { setSymbol(event.target.value); setDatasetId(undefined); }} placeholder="ES=F, EURUSD=X, ^GSPC" />
            {provider === 'yahoo' && hits.length > 0 && (
              <div className="mt-1 max-h-36 overflow-auto rounded-md border border-zinc-200 dark:border-zinc-700">
                {hits.map((hit) => (
                  <button key={hit.symbol} type="button" className="block w-full px-2 py-1 text-left text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => { setSymbol(hit.symbol); setHits([]); }}>
                    <span className="font-medium">{hit.symbol}</span>
                    <span className="ml-2 text-zinc-500">{hit.name} · {hit.type} {hit.exchange}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-1 flex flex-wrap gap-1">
              {quick.map((item) => (
                <button key={item.symbol} type="button" className="rounded-full border border-zinc-300 px-2 py-0.5 text-[11px] dark:border-zinc-700" onClick={() => setSymbol(item.symbol)}>{item.label}</button>
              ))}
            </div>
          </Field>
          {provider === 'csv' && (
            <Field label="CSV file">
              <Input type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void onUpload(file); }} />
              <p className="mt-1 text-[11px] text-zinc-500">Header: time, open, high, low, close, volume.</p>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Timeframe">
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['1m', '5m', '15m', '1h', '1d'].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Validation">
              <Select value={validation} onValueChange={setValidation}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Full sample</SelectItem>
                  <SelectItem value="is_oos">In / out of sample</SelectItem>
                  <SelectItem value="walk_forward">Walk-forward</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="From"><Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></Field>
            <Field label="To"><Input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></Field>
          </div>
          {limit && (
            <p className={`text-[11px] ${tooLong ? 'text-red-600' : 'text-zinc-500'}`}>
              {limit.note} This range is {days} days.
              {tooLong && (
                <button type="button" className="ml-2 underline" onClick={() => setFrom(isoDaysAgo(limit.maxDays - 1))}>Clip to the limit</button>
              )}
            </p>
          )}
          <Field label="Strategy">
            <Select value={strategyId} onValueChange={(value) => { setStrategyId(value); setParams({}); setSweeps({}); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {strategies.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {strategy && <p className="mt-1 text-[11px] leading-snug text-zinc-500">{strategy.summary}</p>}
          </Field>
          <div className="space-y-2">
            {(strategy?.params || []).map((field) => (
              <ParamRow
                key={field.key}
                field={field}
                value={params[field.key]}
                sweep={sweeps[field.key]}
                onChange={(value) => setParams((curr) => ({ ...curr, [field.key]: value }))}
                onSweep={(next) => setSweeps((curr) => {
                  const copy = { ...curr };
                  if (!next) delete copy[field.key];
                  else copy[field.key] = next;
                  return copy;
                })}
              />
            ))}
          </div>
          {validation !== 'none' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="In-sample fraction"><Input value={isFraction} onChange={(event) => setIsFraction(event.target.value)} /></Field>
              {validation === 'walk_forward' && <Field label="Folds"><Input value={folds} onChange={(event) => setFolds(event.target.value)} /></Field>}
              <Field label="Pick variants by">
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expectancyR">Expectancy (R)</SelectItem>
                    <SelectItem value="netPnl">Net P&L</SelectItem>
                    <SelectItem value="profitFactor">Profit factor</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {validation === 'walk_forward' && (
                <label className="flex items-center gap-2 text-xs">
                  <Checkbox checked={anchored} onCheckedChange={(value) => setAnchored(value === true)} />
                  Anchored train window
                </label>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Spread (points)">
              <Input value={spread} placeholder="preset" onChange={(event) => setSpread(event.target.value)} />
            </Field>
            <Field label="Slippage (points)"><Input value={slippage} onChange={(event) => setSlippage(event.target.value)} /></Field>
            <Field label="Long funding / year"><Input value={fundLong} onChange={(event) => setFundLong(event.target.value)} /></Field>
            <Field label="Short funding / year"><Input value={fundShort} onChange={(event) => setFundShort(event.target.value)} /></Field>
            <Field label="Max spread / stop"><Input value={maxSpread} placeholder="blank = off" onChange={(event) => setMaxSpread(event.target.value)} /></Field>
            <Field label="Sizing">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="risk_gbp">£ risk, live stake band</SelectItem>
                  <SelectItem value="fixed_stake">Fixed stake</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Starting capital"><Input value={capital} onChange={(event) => setCapital(event.target.value)} /></Field>
            {mode === 'risk_gbp' ? (
              <>
                <Field label="Risk £"><Input value={risk} onChange={(event) => setRisk(event.target.value)} /></Field>
                <Field label="Risk cap £"><Input value={cap} onChange={(event) => setCap(event.target.value)} /></Field>
                <Field label="Stake min"><Input value={stakeMin} onChange={(event) => setStakeMin(event.target.value)} /></Field>
                <Field label="Stake max"><Input value={stakeMax} onChange={(event) => setStakeMax(event.target.value)} /></Field>
              </>
            ) : (
              <Field label="Stake £/pt"><Input value={fixedStake} onChange={(event) => setFixedStake(event.target.value)} /></Field>
            )}
          </div>
          {spreadNote && <p className="text-[11px] text-zinc-500">{spreadNote} Leave spread blank to use it.</p>}
          {err && <p className="text-sm text-red-600">{err}</p>}
          <Button type="button" className="w-full" disabled={busy || tooLong || run?.status === 'running' || run?.status === 'queued'} onClick={() => void onRun()}>
            {run?.status === 'running' || run?.status === 'queued' ? 'Running…' : 'Run backtest'}
          </Button>
        </section>

        <section className="space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold">Saved runs</h2>
              <Button type="button" size="sm" variant="outline" disabled={picked.length < 2} onClick={() => void onCompare()}>Compare</Button>
            </div>
            {history.length === 0 && <p className="text-xs text-zinc-500">No saved runs yet.</p>}
            <ul className="max-h-48 space-y-1 overflow-auto">
              {history.map((item) => (
                <li key={item.id} className="flex items-center gap-2 text-xs">
                  <Checkbox
                    checked={picked.includes(item.id)}
                    onCheckedChange={(value) => setPicked((curr) => {
                      if (value === true) return curr.includes(item.id) || curr.length >= 4 ? curr : [...curr, item.id];
                      return curr.filter((id) => id !== item.id);
                    })}
                    aria-label={`Compare ${item.name}`}
                  />
                  <button type="button" className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => { setRunId(item.id); setSegmentKey('primary'); }}>
                    {item.name}
                    <span className="ml-2 text-zinc-500">{item.status}{item.headline ? ` · ${signed(item.headline.netPnl)} · ${item.headline.trades}` : ''}</span>
                  </button>
                  <button type="button" className="text-zinc-400 hover:text-red-600" onClick={() => void removeRun(item.id)} aria-label="Delete run">Delete</button>
                </li>
              ))}
            </ul>
          </div>

          {run && (run.status === 'running' || run.status === 'queued') && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="mb-1 flex justify-between text-xs"><span>{run.progress.message}</span><span>{run.progress.pct}%</span></div>
              <div className="h-2 overflow-hidden rounded bg-zinc-200 dark:bg-zinc-800"><div className="h-full bg-zinc-900 dark:bg-zinc-100" style={{ width: `${run.progress.pct}%` }} /></div>
            </div>
          )}
          {run?.status === 'error' && <p className="text-sm text-red-600">{run.error}</p>}

          {compare && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <h2 className="mb-2 text-sm font-semibold">Comparison</h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Run</TableHead>
                      <TableHead>Focus</TableHead>
                      <TableHead>Net</TableHead>
                      <TableHead>Trades</TableHead>
                      <TableHead>Win</TableHead>
                      <TableHead>Avg R</TableHead>
                      <TableHead>PF</TableHead>
                      <TableHead>Max DD</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compare.runs.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.label}</TableCell>
                        <TableCell className={colour(item.metrics.netPnl)}>{signed(item.metrics.netPnl)}</TableCell>
                        <TableCell>{item.metrics.trades}</TableCell>
                        <TableCell>{pct(item.metrics.winRate)}</TableCell>
                        <TableCell>{num(item.metrics.expectancyR)}</TableCell>
                        <TableCell>{num(item.metrics.profitFactor)}</TableCell>
                        <TableCell>{money(item.metrics.maxDrawdown)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <ChartContainer config={compareConfig(compare.runs)} className="mt-3 aspect-auto h-[220px] w-full">
                <LineChart data={compare.equity}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="t" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis tickLine={false} axisLine={false} fontSize={11} width={56} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  {compare.runs.map((item, index) => (
                    <Line key={item.id} type="monotone" dataKey={item.id} name={item.name} stroke={COMPARE_COLOURS[index % COMPARE_COLOURS.length]} dot={false} strokeWidth={2} connectNulls />
                  ))}
                </LineChart>
              </ChartContainer>
            </div>
          )}

          {result && segment && (
            <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold">{result.summary.name} · {result.summary.symbol} · {result.summary.timeframe}</h2>
                  <p className="text-[11px] text-zinc-500">{result.summary.barCount} bars · {result.summary.variantCount} variant{result.summary.variantCount === 1 ? '' : 's'} · spread {result.summary.spreadPoints} pt · {result.summary.spreadNote}</p>
                </div>
                <span className="flex flex-wrap gap-2">
                  {result.full && <Button type="button" size="sm" variant={segmentKey === 'full' ? 'default' : 'outline'} onClick={() => setSegmentKey('full')}>Full</Button>}
                  {result.inSample && <Button type="button" size="sm" variant={segmentKey === 'inSample' ? 'default' : 'outline'} onClick={() => setSegmentKey('inSample')}>In sample</Button>}
                  <Button type="button" size="sm" variant={segmentKey === 'primary' ? 'default' : 'outline'} onClick={() => setSegmentKey('primary')}>{result.primary.label}</Button>
                  {run && <Button type="button" size="sm" variant="outline" asChild><a href={`/api/backtests/runs/${run.id}/trades.csv`}>Export CSV</a></Button>}
                  {run && <Button type="button" size="sm" variant="outline" onClick={() => { void api(`/runs/${run.id}/rerun`, { method: 'POST' }).then((created) => setRunId(created.id)).catch((error: Error) => setErr(error.message)); }}>Re-run</Button>}
                </span>
              </div>
              {result.summary.warnings.map((warning) => <p key={warning} className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>)}
              <Badge variant="outline">{segment.label}</Badge>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Metric label="Net P&L" value={signed(segment.metrics.netPnl)} className={colour(segment.metrics.netPnl)} />
                <Metric label="Trades" value={String(segment.metrics.trades)} hint={`${segment.metrics.wins} won · ${segment.metrics.losses} lost`} />
                <Metric label="Win rate" value={pct(segment.metrics.winRate)} />
                <Metric label="Avg R" value={num(segment.metrics.avgR)} />
                <Metric label="Profit factor" value={segment.metrics.profitFactor === null ? '∞' : num(segment.metrics.profitFactor)} />
                <Metric label="Expectancy" value={segment.metrics.expectancyGbp === null ? '—' : signed(segment.metrics.expectancyGbp)} hint={segment.metrics.expectancyR === null ? '' : `${num(segment.metrics.expectancyR)}R`} />
                <Metric label="Max drawdown" value={money(segment.metrics.maxDrawdown)} hint={pct(segment.metrics.maxDrawdownPct)} />
                <Metric label="Sharpe" value={num(segment.metrics.sharpe)} hint={`Exposure ${pct(segment.metrics.exposure)}`} />
              </div>
              <p className="text-[11px] text-zinc-500">
                Skipped {segment.metrics.skipped.riskCap} over the risk cap, {segment.metrics.skipped.spread} on spread, {segment.metrics.skipped.invalid} invalid.
                Longest losing streak {segment.metrics.maxConsecutiveLosses}.
              </p>
              {chart.length > 1 && (
                <>
                  <ChartContainer config={equityConfig} className="aspect-auto h-[220px] w-full">
                    <AreaChart data={chart}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="idx" type="number" domain={[0, chart.length - 1]} tickFormatter={(idx) => chart[Number(idx)]?.when || ''} tickLine={false} axisLine={false} fontSize={11} />
                      <YAxis tickLine={false} axisLine={false} width={56} fontSize={11} />
                      <ChartTooltip content={<ChartTooltipContent labelFormatter={(_v, payload) => payload?.[0]?.payload?.when || ''} />} />
                      <Area dataKey="equity" type="monotone" stroke="var(--color-equity)" fill="var(--color-equity)" fillOpacity={0.15} />
                    </AreaChart>
                  </ChartContainer>
                  <ChartContainer config={ddConfig} className="aspect-auto h-[160px] w-full">
                    <AreaChart data={chart}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="idx" type="number" domain={[0, chart.length - 1]} tickFormatter={(idx) => chart[Number(idx)]?.when || ''} tickLine={false} axisLine={false} fontSize={11} />
                      <YAxis tickLine={false} axisLine={false} width={56} fontSize={11} />
                      <ChartTooltip content={<ChartTooltipContent labelFormatter={(_v, payload) => payload?.[0]?.payload?.when || ''} />} />
                      <Area dataKey="dd" type="monotone" stroke="var(--color-dd)" fill="var(--color-dd)" fillOpacity={0.2} />
                    </AreaChart>
                  </ChartContainer>
                </>
              )}
              <PeriodTable title="By year" rows={segment.yearly} />
              <PeriodTable title="By month" rows={segment.monthly} />
              {result.sweep && result.sweep.rows.length > 0 && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold">{result.sweep.count} variants, top {result.sweep.rows.length} shown</h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Settings</TableHead><TableHead>Trades</TableHead><TableHead>Net</TableHead><TableHead>Avg R</TableHead><TableHead>OOS net</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {result.sweep.rows.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell className="max-w-[240px] truncate text-xs">{JSON.stringify(row.params)}</TableCell>
                            <TableCell>{row.trades}</TableCell>
                            <TableCell>{signed(row.netPnl)}</TableCell>
                            <TableCell>{num(row.expectancyR)}</TableCell>
                            <TableCell>{row.outOfSampleNetPnl === undefined ? '—' : signed(row.outOfSampleNetPnl)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {result.folds && result.folds.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Fold</TableHead><TableHead>Train net</TableHead><TableHead>Test net</TableHead><TableHead>Test avg R</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {result.folds.map((fold) => (
                        <TableRow key={fold.index}>
                          <TableCell>{fold.index}</TableCell>
                          <TableCell>{signed(fold.train.netPnl)}</TableCell>
                          <TableCell>{signed(fold.test.netPnl)}</TableCell>
                          <TableCell>{num(fold.test.expectancyR)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Entry</TableHead><TableHead>Exit</TableHead><TableHead>Side</TableHead><TableHead>Prices</TableHead><TableHead>Stake</TableHead><TableHead>P&L</TableHead><TableHead>R</TableHead><TableHead>Why</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {segment.trades.slice(0, tradeLimit).map((trade) => (
                      <TableRow key={trade.id}>
                        <TableCell className="whitespace-nowrap text-xs">{when(trade.entryTime)}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">{when(trade.exitTime)}</TableCell>
                        <TableCell>{trade.side}</TableCell>
                        <TableCell className="text-xs">{trade.entry.toFixed(4)} → {trade.exit.toFixed(4)}</TableCell>
                        <TableCell>{trade.stake}</TableCell>
                        <TableCell className={colour(trade.pnl)}>{signed(trade.pnl)}</TableCell>
                        <TableCell>{num(trade.r)}</TableCell>
                        <TableCell className="text-xs">{trade.exitReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {segment.trades.length > tradeLimit && (
                <Button type="button" variant="outline" size="sm" onClick={() => setTradeLimit((value) => value + 50)}>Show more trades</Button>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="block space-y-1 text-xs">
      <Label className="text-xs text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}

function Metric({ label, value, hint, className }: { label: string; value: string; hint?: string; className?: string }) {
  return (
    <div className="rounded-md border border-zinc-200 p-2 dark:border-zinc-800">
      <div className="text-[11px] text-zinc-500">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${className || ''}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}

function PeriodTable({ title, rows }: { title: string; rows: Segment['yearly'] }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold">{title}</h3>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Trades</TableHead><TableHead>Wins</TableHead><TableHead>Net</TableHead><TableHead>Avg R</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.period}>
                <TableCell>{row.period}</TableCell>
                <TableCell>{row.trades}</TableCell>
                <TableCell>{row.wins}</TableCell>
                <TableCell className={colour(row.netPnl)}>{signed(row.netPnl)}</TableCell>
                <TableCell>{num(row.avgR)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ParamRow({
  field, value, sweep, onChange, onSweep,
}: {
  field: ParamField;
  value: ParamValue | undefined;
  sweep?: { min: string; max: string; step: string };
  onChange: (value: ParamValue) => void;
  onSweep: (next: { min: string; max: string; step: string } | null) => void;
}) {
  const shown = value === undefined ? field.default : value;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-zinc-500">{field.label}</Label>
        {field.type === 'number' && (
          <label className="flex items-center gap-1 text-[11px] text-zinc-500">
            <Checkbox checked={Boolean(sweep)} onCheckedChange={(checked) => onSweep(checked === true ? { min: String(field.min ?? shown), max: String(field.max ?? shown), step: String(field.step ?? 1) } : null)} />
            Sweep
          </label>
        )}
      </div>
      {field.type === 'boolean' ? (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={shown === true} onCheckedChange={(checked) => onChange(checked === true)} />
          {shown === true ? 'On' : 'Off'}
        </label>
      ) : field.type === 'select' ? (
        <Select value={String(shown)} onValueChange={onChange}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={String(shown)}
          onChange={(event) => {
            if (field.type === 'number') {
              const parsed = Number(event.target.value);
              if (Number.isFinite(parsed)) onChange(parsed);
              return;
            }
            onChange(event.target.value);
          }}
        />
      )}
      {field.help && <p className="text-[11px] text-zinc-500">{field.help}</p>}
      {sweep && (
        <div className="grid grid-cols-3 gap-1">
          <Input value={sweep.min} onChange={(event) => onSweep({ ...sweep, min: event.target.value })} aria-label={`${field.label} sweep min`} />
          <Input value={sweep.max} onChange={(event) => onSweep({ ...sweep, max: event.target.value })} aria-label={`${field.label} sweep max`} />
          <Input value={sweep.step} onChange={(event) => onSweep({ ...sweep, step: event.target.value })} aria-label={`${field.label} sweep step`} />
        </div>
      )}
    </div>
  );
}

const equityConfig: ChartConfig = { equity: { label: 'Equity', color: '#0f766e' } };
const ddConfig: ChartConfig = { dd: { label: 'Drawdown', color: '#b91c1c' } };
const COMPARE_COLOURS = ['#0f766e', '#1d4ed8', '#b45309', '#7c3aed'];

function compareConfig(runs: { id: string; name: string }[]): ChartConfig {
  const config: ChartConfig = {};
  runs.forEach((run, index) => {
    config[run.id] = { label: run.name, color: COMPARE_COLOURS[index % COMPARE_COLOURS.length] };
  });
  return config;
}
